import React, { useState, useEffect } from "react";
import {
  createGrade,
  updateGrade,
  deleteGrade,
  getGrades,
} from "../../../utils/grades";
import useUserStore from "../../../stores/user";
import { toast } from "react-toastify";
import { useT } from "../../../stores/uiLang";
import { TD_T } from "../../../i18n/teacherDashboard";

// Predefined grade scales
export const PREDEFINED_SCALES = [
  {
    id: "standard-letter",
    name: "Standard Letter Grade (A-F)",
    values: [
      { label: "A", min: 90, max: 100, default: 95 },
      { label: "B", min: 80, max: 89, default: 85 },
      { label: "C", min: 70, max: 79, default: 75 },
      { label: "D", min: 60, max: 69, default: 65 },
      { label: "F", min: 0, max: 59, default: 50 },
    ],
    isPredefined: true,
  },
  {
    id: "complete-incomplete",
    name: "Complete / Incomplete",
    values: [
      { label: "Complete", min: 100, max: 100, default: 100 },
      { label: "Incomplete", min: 0, max: 99.99, default: 0 },
    ],
    isPredefined: true,
  },
  {
    id: "pass-fail",
    name: "Pass / Fail",
    values: [
      { label: "Pass", min: 70, max: 100, default: 100 },
      { label: "Fail", min: 0, max: 69, default: 0 },
    ],
    isPredefined: true,
  },
  {
    id: "proficiency-4point",
    name: "4-Point Proficiency Scale",
    values: [
      { label: "4 - Exceeds Standard", min: 100, max: 100, default: 100 },
      { label: "3 - Meets Standard", min: 85, max: 99, default: 85 },
      { label: "2 - Approaching Standard", min: 70, max: 84, default: 70 },
      { label: "1 - Not Met", min: 0, max: 69, default: 50 },
    ],
    isPredefined: true,
  },
];

function emptyCustomScale() {
  return {
    id: undefined,
    name: "",
    values: [{ label: "", min: 0, max: 100, default: 100 }],
    isPredefined: false,
  };
}

export default function GradeScalesManager() {
  const t = useT(TD_T);
  const [customScales, setCustomScales] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingScale, setEditingScale] = useState<any | null>(null);
  const [pendingDelete, setPendingDelete] = useState<any | null>(null);
  const { user } = useUserStore();

  useEffect(() => {
    if (user?.id) {
      getGrades(user.id).then(({ grades, error }) => {
        if (error) {
          console.error("Failed to fetch grade scales:", error);
        } else {
          setCustomScales(grades);
        }
      });
    }
  }, [user?.id]);

  // Handlers
  const handleAdd = () => {
    setEditingScale(emptyCustomScale());
    setModalOpen(true);
  };
  const handleEdit = (scale: any) => {
    setEditingScale({ ...scale });
    setModalOpen(true);
  };
  const handleDelete = async (id: string) => {
    setPendingDelete(id);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const teacher_id = user?.id;
    const result = await deleteGrade(pendingDelete, teacher_id);
    if (result.error) {
      toast.error("Failed to delete grade scale");
    } else {
      setCustomScales((prev) => prev.filter((s) => s.id !== pendingDelete));
      toast.success("Grade scale deleted");
    }
    setPendingDelete(null);
  };

  const cancelDelete = () => {
    setPendingDelete(null);
  };
  const handleModalSave = async (scale: any) => {
    const teacher_id = user?.id;
    if (scale.id) {
      // Update existing
      const result = await updateGrade(scale.id, teacher_id, {
        name: scale.name,
        values: scale.values,
      });
      if (result.error) {
        alert("Failed to update grade scale: " + result.error);
        return;
      }
      setCustomScales((prev) =>
        prev.map((s) => (s.id === scale.id ? scale : s))
      );
    } else {
      // Persist to backend
      try {
        const result = await createGrade({
          name: scale.name,
          values: scale.values,
        });
        if (result.error) {
          toast.error("Failed to create grade scale");
        } else {
          // Use the id returned from the backend, not a random id
          if (result.grade_scale && result.grade_scale.id) {
            setCustomScales((prev) => [...prev, result.grade_scale]);
          } else {
            // fallback: fetch all grades
            getGrades(user?.id).then(({ grades }) => setCustomScales(grades));
          }
        }
      } catch (err: any) {
        console.error("Unexpected error: " + err.message);
      }
    }
    setModalOpen(false);
    setEditingScale(null);
  };
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingScale(null);
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">{t.gradeScales}</h2>
      <div className="flex items-center justify-between mb-4">
        <button
          className="btn btn-primary px-3 py-1 rounded"
          onClick={handleAdd}
        >
          + {t.addScale}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* Custom Scales First */}
        {customScales.length === 0 ? (
          <div className="text-gray-500 col-span-2">{t.noCustomScales}</div>
        ) : (
          customScales.map((scale) => (
            <div key={scale.id} className="border rounded p-4 bg-white">
              <div className="font-semibold flex items-center justify-between">
                {scale.name}
                <span>
                  <button
                    className="text-blue-600 hover:underline mr-2"
                    onClick={() => handleEdit(scale)}
                  >
                    {t.edit}
                  </button>
                  <button
                    className="text-red-500 hover:underline"
                    onClick={() => handleDelete(scale.id)}
                  >
                    {t.delete}
                  </button>
                </span>
              </div>
              <ul className="text-sm mt-2">
                {scale.values.map((v, i) => (
                  <li key={i}>
                    {v.label}: {v.min} {v.max !== v.min ? `-${v.max}` : ""}%
                    (default: {v.default}%)
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
        {/* Predefined Scales Next */}
        {PREDEFINED_SCALES.map((scale) => (
          <div key={scale.id} className="border rounded p-4 bg-gray-50">
            <div className="font-semibold flex items-center justify-between">
              {scale.name}
            </div>
            <ul className="text-sm mt-2">
              {scale.values.map((v, i) => (
                <li key={i}>
                  {v.label}: {v.min}
                  {v.max !== v.min ? `-${v.max}` : ""}% (default: {v.default}
                  %)
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {modalOpen && (
        <GradeScaleModal
          scale={editingScale}
          onSave={handleModalSave}
          onClose={handleModalClose}
        />
      )}
      {/* Confirm Delete Modal */}
      {pendingDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              {t.deleteScaleTitle}
            </h3>
            <p className="mb-6 text-gray-700">
              {t.deleteScaleConfirm}
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="btn btn-secondary px-3 py-1 rounded"
                onClick={cancelDelete}
              >
                {t.cancel}
              </button>
              <button
                className="btn btn-danger px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={confirmDelete}
              >
                {t.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Modal for add/edit scale
function GradeScaleModal({ scale, onSave, onClose }) {
  const t = useT(TD_T);
  const [form, setForm] = useState<any>({ ...scale });

  const handleValueChange = (idx, field, value) => {
    setForm((prev) => {
      const values = [...prev.values];
      values[idx] = { ...values[idx], [field]: value };
      return { ...prev, values };
    });
  };
  const handleAddValue = () => {
    setForm((prev) => ({
      ...prev,
      values: [...prev.values, { label: "", min: 0, max: 100, default: 100 }],
    }));
  };
  const handleRemoveValue = (idx) => {
    setForm((prev) => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== idx),
    }));
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded shadow-lg p-6 w-full max-w-lg border border-gray-200">
        <h3 className="text-2xl font-semibold mb-6 text-gray-800">
          {form.id ? "Edit" : "Add"} Grade Scale
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-semibold mb-1">
              {t.scaleName}
            </label>
            <input
              className="border rounded px-2 py-1 w-full"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              placeholder={t.scaleNamePh}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2">
              {t.scaleValues}
            </label>
            {/* Header row for value fields */}
            <div className="flex items-center gap-2 mb-1 pl-1">
              <span className="w-24 text-xs font-semibold text-gray-700">
                {t.label}
              </span>
              <span className="w-20 text-xs font-semibold text-gray-700">
                Min %
              </span>
              <span className="w-20 text-xs font-semibold text-gray-700">
                Max %
              </span>
              <span className="w-20 text-xs font-semibold text-gray-700">
                Default %
              </span>
              <span className="w-16"></span>
            </div>
            {form.values.map((v, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-3">
                <input
                  className="border rounded px-2 py-1 w-24"
                  placeholder={t.label}
                  value={v.label}
                  onChange={(e) =>
                    handleValueChange(idx, "label", e.target.value)
                  }
                  required
                />
                <input
                  className="border rounded px-2 py-1 w-20"
                  type="number"
                  placeholder="Min %"
                  value={v.min}
                  onChange={(e) =>
                    handleValueChange(idx, "min", Number(e.target.value))
                  }
                  required
                  min={0}
                  max={100}
                />
                <input
                  className="border rounded px-2 py-1 w-20"
                  type="number"
                  placeholder="Max %"
                  value={v.max}
                  onChange={(e) =>
                    handleValueChange(idx, "max", Number(e.target.value))
                  }
                  required
                  min={0}
                  max={100}
                />
                <input
                  className="border rounded px-2 py-1 w-20"
                  type="number"
                  placeholder="Default %"
                  value={v.default}
                  onChange={(e) =>
                    handleValueChange(idx, "default", Number(e.target.value))
                  }
                  required
                  min={0}
                  max={100}
                />
                <button
                  type="button"
                  className="text-red-600 font-semibold hover:underline ml-2"
                  onClick={() => handleRemoveValue(idx)}
                  disabled={form.values.length === 1}
                  title="Remove row"
                >
                  {t.remove}
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-blue-700 font-semibold hover:underline mt-2"
              onClick={handleAddValue}
            >
              + Add Value
            </button>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              className="btn btn-secondary px-3 py-1 rounded"
              onClick={onClose}
            >
              {t.cancel}
            </button>
            <button type="submit" className="btn btn-primary px-3 py-1 rounded">
              {t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
